/**
 * MCP 服务配置 Schema 定义
 *
 * 统一配置框架：按服务名声明配置表单字段。
 * - 内置适配器（storage: 'settings'）→ 存入 settings 的 mcp_adapter_config[name]
 * - 外部 MCP 服务（storage: 'mcpConfig'）→ 写入 mcp_config.json 的 command/args/env
 */

export interface MCPConfigField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'textarea';
  required?: boolean;
  placeholder?: string;
  hint?: string;
}

export interface MCPConfigSchema {
  /** 'settings' = 内置适配器，存 mcp_adapter_config；'mcpConfig' = 外部服务，写 mcp_config.json */
  storage: 'settings' | 'mcpConfig';
  /**
   * 外部预置服务的 args 映射方式：
   * - 'paths'：把指定 pathsField 的多行值替换 args 中包名之后的全部路径参数
   * - 'generic'：通用 command/args/env 表单，直接整体覆盖
   */
  argsMapping?: 'paths' | 'generic';
  /** argsMapping='paths' 时，承载路径列表的字段 key */
  pathsField?: string;
  fields: MCPConfigField[];
}

/** 从 npx 风格 args 中拆分 [包调用前缀, 路径参数列表] */
export function splitArgs(args: string[]): { prefix: string[]; paths: string[] } {
  const pkgIdx = args.findIndex(a => a.includes('server-') || a.includes('mcp-') || a.startsWith('@'));
  if (pkgIdx >= 0) {
    return { prefix: args.slice(0, pkgIdx + 1), paths: args.slice(pkgIdx + 1) };
  }
  // 无法识别包名时，保守保留前两个 token（如 -y + 包名）
  return { prefix: args.slice(0, Math.min(2, args.length)), paths: args.slice(Math.min(2, args.length)) };
}

const BUILTIN_SCHEMAS: Record<string, MCPConfigSchema> = {
  wechat_bot: {
    storage: 'settings',
    fields: [
      { key: 'appId', label: 'AppID', type: 'text', required: true, placeholder: '微信公众号 AppID' },
      { key: 'appSecret', label: 'AppSecret', type: 'password', required: true, placeholder: '微信公众号 AppSecret' },
      { key: 'token', label: 'Token', type: 'text', placeholder: '服务器配置令牌（自定义）' },
      { key: 'encodingAesKey', label: 'EncodingAESKey', type: 'text', placeholder: '消息加解密密钥（43 位）' },
    ],
  },
  enterprise_wechat_scrm: {
    storage: 'settings',
    fields: [
      { key: 'corpId', label: '企业 ID (CorpID)', type: 'text', required: true, placeholder: '企业微信 CorpID' },
      { key: 'corpSecret', label: '应用 Secret', type: 'password', required: true, placeholder: '企业微信应用 Secret' },
      { key: 'agentId', label: 'AgentID', type: 'text', placeholder: '应用 AgentID' },
    ],
  },
  feishu_minutes: {
    storage: 'settings',
    fields: [
      { key: 'appId', label: 'App ID', type: 'text', required: true, placeholder: '飞书开放平台 App ID' },
      { key: 'appSecret', label: 'App Secret', type: 'password', required: true, placeholder: '飞书开放平台 App Secret' },
    ],
  },
  dingtalk_automation: {
    storage: 'settings',
    fields: [
      { key: 'appKey', label: 'AppKey', type: 'text', required: true, placeholder: '钉钉开放平台 AppKey' },
      { key: 'appSecret', label: 'AppSecret', type: 'password', required: true, placeholder: '钉钉开放平台 AppSecret' },
      { key: 'robotCode', label: '机器人 Code', type: 'text', placeholder: '群机器人 RobotCode' },
    ],
  },
  iot_gateway: {
    storage: 'settings',
    fields: [
      { key: 'brokerUrl', label: 'Broker 地址', type: 'text', required: true, placeholder: 'mqtt://broker.local:1883' },
      { key: 'username', label: '用户名', type: 'text', placeholder: 'MQTT 用户名（可选）' },
      { key: 'password', label: '密码', type: 'password', placeholder: 'MQTT 密码（可选）' },
    ],
  },
  rpa_federation: { storage: 'settings', fields: [] },
  agent_collaboration: { storage: 'settings', fields: [] },
};

const PRESET_EXTERNAL_SCHEMAS: Record<string, MCPConfigSchema> = {
  filesystem: {
    storage: 'mcpConfig',
    argsMapping: 'paths',
    pathsField: 'allowedDirs',
    fields: [
      {
        key: 'allowedDirs', label: '允许访问的目录', type: 'textarea', required: true,
        placeholder: '每行一个目录路径，例如：\nG:\\mywoke\nD:\\documents',
        hint: '仅这些目录对智能体可见，保存后服务自动重连',
      },
    ],
  },
  sqlite: {
    storage: 'mcpConfig',
    argsMapping: 'paths',
    pathsField: 'dbPath',
    fields: [
      {
        key: 'dbPath', label: '数据库文件路径', type: 'textarea', required: true,
        placeholder: 'SQLite 数据库文件路径，例如：data/xiaofengos.db',
      },
    ],
  },
  git: {
    storage: 'mcpConfig',
    argsMapping: 'paths',
    pathsField: 'repoPath',
    fields: [
      {
        key: 'repoPath', label: '仓库路径', type: 'textarea', required: true,
        placeholder: 'Git 仓库目录，例如：G:\\mywoke',
      },
    ],
  },
};

/** 通用外部服务表单（未知服务兜底） */
const GENERIC_EXTERNAL_SCHEMA: MCPConfigSchema = {
  storage: 'mcpConfig',
  argsMapping: 'generic',
  fields: [
    { key: 'command', label: '启动命令', type: 'text', required: true, placeholder: 'npx / node / python ...' },
    { key: 'args', label: '启动参数', type: 'text', placeholder: '空格分隔，如：-y @modelcontextprotocol/server-filesystem .' },
    { key: 'env', label: '环境变量 (JSON)', type: 'textarea', placeholder: '{"API_KEY":"..."}' },
  ],
};

/**
 * 获取服务的配置 schema：
 * 内置适配器 → 预置表单；预置外部服务（filesystem/sqlite/git）→ 路径表单；其他外部 → 通用表单。
 */
export function getConfigSchema(name: string, source?: string): MCPConfigSchema {
  if (source === 'builtin' || BUILTIN_SCHEMAS[name]) return BUILTIN_SCHEMAS[name] || { storage: 'settings', fields: [] };
  return PRESET_EXTERNAL_SCHEMAS[name] || GENERIC_EXTERNAL_SCHEMA;
}

/** schema 是否含必填字段（用于开关联动：未配置时先弹配置页） */
export function hasRequiredFields(schema: MCPConfigSchema): boolean {
  return schema.fields.some(f => f.required);
}

/** 校验已存配置是否满足必填项 */
export function isConfigComplete(schema: MCPConfigSchema, values: Record<string, string>): boolean {
  return schema.fields.every(f => !f.required || (values[f.key] || '').trim().length > 0);
}
